import ChatProposalStatus from "../../enum/chat/ChatProposalStatus";
import {
  ChatChangeDealStageAction,
  ChatCustomFieldValueRaw,
  ChatDealStageRef,
  ChatLogActivityAction,
  ChatLogNoteAction,
  ChatProposal,
  ChatProposalAction,
  UNKNOWN_PROPOSAL_ACTION,
} from "../../types/chat/ChatContent";
import { ChatContextEntityType } from "../../types/chat/ChatMessage";

import { CONTEXT_ENTITY_TYPES, isRecord, RECORD_PARENT_ENTITY_TYPES } from "./chatWireGuards";
import parseCreateRecordAction from "./parseCreateRecordAction";
import parseCreateRouteAction from "./parseCreateRouteAction";
import parseManageRelationshipAction from "./parseManageRelationshipAction";
import parseManageRouteStopsAction from "./parseManageRouteStopsAction";
import parseRecordUpdateAction from "./parseRecordUpdateAction";
import parseRouteStopPickAction from "./parseRouteStopPickAction";
import parseSendEmailAction from "./parseSendEmailAction";

const parseLogNoteAction = (value: Record<string, unknown>): ChatLogNoteAction | undefined => {
  const entityType = CONTEXT_ENTITY_TYPES.find((candidate) => candidate === value.entityType);
  return typeof value.body === "string" &&
    typeof value.displayName === "string" &&
    typeof value.entityId === "number" &&
    entityType
    ? {
        body: value.body,
        displayName: value.displayName,
        entityId: value.entityId,
        entityType,
        type: "log_note",
      }
    : undefined;
};

const parseDealStageRef = (value: unknown): ChatDealStageRef | undefined => {
  if (typeof value !== "object" || value === null) {
    return undefined;
  }
  const { id, name } = value as Record<string, unknown>;
  return typeof id === "number" && typeof name === "string" ? { id, name } : undefined;
};

const parseChangeDealStageAction = (
  raw: Record<string, unknown>,
  warnings: string[]
): ChatChangeDealStageAction | undefined => {
  const currentStage = parseDealStageRef(raw.currentStage);
  const updatedStage = parseDealStageRef(raw.updatedStage);
  if (
    !currentStage ||
    !updatedStage ||
    typeof raw.displayName !== "string" ||
    typeof raw.entityId !== "number" ||
    !CONTEXT_ENTITY_TYPES.find((candidate) => candidate === raw.entityType) ||
    typeof raw.funnelId !== "number"
  ) {
    return undefined;
  }
  const lossReasonRaw =
    typeof raw.lossReason === "object" && raw.lossReason !== null
      ? (raw.lossReason as Record<string, unknown>)
      : undefined;
  const lossReason =
    typeof lossReasonRaw?.id === "number" && typeof lossReasonRaw?.name === "string"
      ? { id: lossReasonRaw.id, name: lossReasonRaw.name }
      : undefined;
  // Both fields drop silently only when absent — that is the normal "no loss info yet" case.
  // Present-but-malformed (e.g. a reason with a name but no id) would otherwise also degrade
  // quietly, leaving a lost move with no reason rendered while BE applies one, so each earns its
  // own warning rather than riding the action's silently.
  if (raw.lossReason !== undefined && !lossReason) {
    warnings.push('Malformed "change_deal_stage" lossReason payload');
  }
  if (raw.lossComment !== undefined && typeof raw.lossComment !== "string") {
    warnings.push('Malformed "change_deal_stage" lossComment payload');
  }
  return {
    currentStage,
    displayName: raw.displayName,
    entityId: raw.entityId,
    entityType: raw.entityType as ChatContextEntityType, // narrowed by the guard above
    funnelId: raw.funnelId,
    ...(typeof raw.lossComment === "string" ? { lossComment: raw.lossComment } : {}),
    ...(lossReason ? { lossReason } : {}),
    type: "change_deal_stage",
    updatedStage,
  };
};

// undefined means "present but malformed", which rejects the whole record. An absent key is handled
// by the caller and never reaches here.
//
// The envelope is checked, the value is not: a custom-field value's shape follows its data type (bare
// for single-option, an array for multi-option, one-element for the rest), and the field model is what
// interprets it. Encoding an allowlist of element types here would reject a data type the platform
// grows later — boolean among them — and take the whole card down with it.
const parseCustomFields = (value: unknown): ChatCustomFieldValueRaw[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const parsed: ChatCustomFieldValueRaw[] = [];
  for (const entry of value) {
    if (
      !isRecord(entry) ||
      typeof entry.customFieldId !== "number" ||
      typeof entry.esKey !== "string" ||
      entry.value === undefined
    ) {
      return undefined;
    }
    parsed.push({
      customFieldId: entry.customFieldId,
      esKey: entry.esKey,
      value: entry.value as ChatCustomFieldValueRaw["value"],
    });
  }
  return parsed;
};

const parseLogActivityRecord = (value: unknown): ChatLogActivityAction["record"] | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }
  const activityType = isRecord(value.crmActivityType) ? value.crmActivityType : undefined;
  if (
    typeof activityType?.id !== "number" ||
    typeof activityType.name !== "string" ||
    typeof value.completed !== "boolean" ||
    typeof value.name !== "string" ||
    (value.endAt !== undefined && typeof value.endAt !== "string") ||
    (value.note !== undefined && typeof value.note !== "string") ||
    (value.startAt !== undefined && typeof value.startAt !== "string")
  ) {
    return undefined;
  }
  // An absent key is legal — the backend omits it rather than sending []. A malformed one rejects
  // the record, matching how every other field in this parser degrades.
  const customFields =
    value.customFields === undefined ? [] : parseCustomFields(value.customFields);
  if (!customFields) {
    return undefined;
  }
  return {
    completed: value.completed,
    crmActivityType: { id: activityType.id, name: activityType.name },
    ...(customFields.length > 0 ? { customFields } : {}),
    ...(typeof value.endAt === "string" ? { endAt: value.endAt } : {}),
    name: value.name,
    ...(typeof value.note === "string" ? { note: value.note } : {}),
    ...(typeof value.startAt === "string" ? { startAt: value.startAt } : {}),
  };
};

const parseLogActivityAction = (
  value: Record<string, unknown>
): ChatLogActivityAction | undefined => {
  const record = parseLogActivityRecord(value.record);
  if (!record) {
    return undefined;
  }
  // The parent triple is all-or-nothing. A partial one means we cannot name the record the apply
  // call would write against, so the row degrades to the fallback rather than rendering a card
  // with a blank record — and a standalone activity legitimately has none of the three.
  const hasAnyParent =
    value.displayName !== undefined ||
    value.entityId !== undefined ||
    value.entityType !== undefined;
  if (!hasAnyParent) {
    return { record, type: "log_activity" };
  }
  const entityType = RECORD_PARENT_ENTITY_TYPES.find((candidate) => candidate === value.entityType);
  return entityType && typeof value.displayName === "string" && typeof value.entityId === "number"
    ? {
        displayName: value.displayName,
        entityId: value.entityId,
        entityType,
        record,
        type: "log_activity",
      }
    : undefined;
};

// A proposal whose action we cannot parse still renders — as a fallback row the rep can
// accept or reject — so a malformed action degrades one row, never the whole message.
const parseProposalAction = (value: unknown, warnings: string[]): ChatProposalAction => {
  if (!isRecord(value) || typeof value.type !== "string") {
    warnings.push("Proposal action is not an object with a string type");
    return { rawType: "", type: UNKNOWN_PROPOSAL_ACTION };
  }
  switch (value.type) {
    case "change_deal_stage": {
      const action = parseChangeDealStageAction(value, warnings);
      if (action) {
        return action;
      }
      warnings.push('Malformed "change_deal_stage" proposal action payload');
      return { rawType: value.type, type: UNKNOWN_PROPOSAL_ACTION };
    }
    case "create_record": {
      const action = parseCreateRecordAction(value, warnings);
      if (action) {
        return action;
      }
      warnings.push('Malformed "create_record" proposal action payload');
      return { rawType: value.type, type: UNKNOWN_PROPOSAL_ACTION };
    }
    case "create_route": {
      const action = parseCreateRouteAction(value, warnings);
      if (action) {
        return action;
      }
      warnings.push('Malformed "create_route" proposal action payload');
      return { rawType: value.type, type: UNKNOWN_PROPOSAL_ACTION };
    }
    case "log_note": {
      const action = parseLogNoteAction(value);
      if (action) {
        return action;
      }
      warnings.push('Malformed "log_note" proposal action payload');
      return { rawType: value.type, type: UNKNOWN_PROPOSAL_ACTION };
    }
    case "log_activity": {
      const action = parseLogActivityAction(value);
      if (action) {
        return action;
      }
      warnings.push('Malformed "log_activity" proposal action payload');
      return { rawType: value.type, type: UNKNOWN_PROPOSAL_ACTION };
    }
    case "manage_relationship": {
      const action = parseManageRelationshipAction(value);
      if (action) {
        return action;
      }
      warnings.push('Malformed "manage_relationship" proposal action payload');
      return { rawType: value.type, type: UNKNOWN_PROPOSAL_ACTION };
    }
    case "manage_route_stops": {
      const action = parseManageRouteStopsAction(value, warnings);
      if (action) {
        return action;
      }
      warnings.push('Malformed "manage_route_stops" proposal action payload');
      return { rawType: value.type, type: UNKNOWN_PROPOSAL_ACTION };
    }
    case "send_email": {
      const action = parseSendEmailAction(value);
      if (action) {
        return action;
      }
      warnings.push('Malformed "send_email" proposal action payload');
      return { rawType: value.type, type: UNKNOWN_PROPOSAL_ACTION };
    }
    case "record_update": {
      const action = parseRecordUpdateAction(value, warnings);
      if (action) {
        return action;
      }
      warnings.push('Malformed "record_update" proposal action payload');
      return { rawType: value.type, type: UNKNOWN_PROPOSAL_ACTION };
    }
    default:
      return { rawType: value.type, type: UNKNOWN_PROPOSAL_ACTION };
  }
};

// A picker is the one action whose shape depends on the proposal around it: its candidates ride on
// the proposal ref, not the action. When they are present the target is not chosen yet, whatever
// the action says. One that cannot be read falls through to the ordinary parse, which degrades it.
const parseAction = (proposal: Record<string, unknown>, warnings: string[]): ChatProposalAction => {
  const { action } = proposal;
  if (
    isRecord(action) &&
    action.type === "manage_route_stops" &&
    proposal.suggestedRecords !== undefined
  ) {
    const pick = parseRouteStopPickAction(proposal, action, warnings);
    if (pick) {
      return pick;
    }
  }
  return parseProposalAction(action, warnings);
};

// The status is asserted, not narrowed — the one cast in a mapper that validates everything else.
// BE pins these five with a DB CHECK constraint, so a sixth value needs a migration, and the value
// is checked anyway where it is read: resolveProposalRowStatus() has to test membership regardless,
// because sessionStorage restores proposals no parser ever saw. Narrowing here too would buy a
// second check for the same impossible case.
const parseProposal = (value: unknown, warnings: string[]): ChatProposal | undefined =>
  isRecord(value) &&
  typeof value.expiresAt === "string" &&
  typeof value.proposalId === "number" &&
  typeof value.status === "string"
    ? {
        action: parseAction(value, warnings),
        expiresAt: value.expiresAt,
        proposalId: value.proposalId,
        status: value.status as ChatProposalStatus,
      }
    : undefined;

const parseProposals = (value: unknown, warnings: string[]): ChatProposal[] | undefined => {
  if (!Array.isArray(value) || value.length === 0) {
    return undefined;
  }
  const proposals = value.map((item) => parseProposal(item, warnings));
  return proposals.every(Boolean) ? (proposals as ChatProposal[]) : undefined;
};

export default parseProposals;
